import { HelpCircleIcon } from '@langchain/untitled-ui-icons';

import { ReactNode } from 'react';

import { Skeleton } from '@/components/Skeleton';
import { Text, Tooltip } from '@/design-system';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import { TraceTTLTier } from '@/types/schema';
import { xCount } from '@/utils/stringUtils';

import { PAYMENT_PLAN_PRICING_MAP } from '../utils/constants';
import { useShowPlanStatusBadge } from './hooks/useShowPlanStatusBadge';
import { useTraceCount } from './hooks/useTraceCount';
import { PaymentPlanCardTiers } from './types';

const formatter = new Intl.NumberFormat('en-US', {});

const PlanFeatureTextWithTooltip = ({
  text,
  tooltipItems,
}: {
  text: string | ReactNode;
  tooltipItems: string[];
}) => {
  return (
    <Text>
      {text}
      <Tooltip
        title={
          <div className="px-4 py-2">
            <ul className="flex list-disc flex-col gap-2">
              {tooltipItems.map((t) => (
                <li key={t} className="list-disc">
                  <Text variant="sm" weight="normal">
                    {t}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        }
      >
        <span className="ml-1.5 inline-block align-text-bottom">
          <HelpCircleIcon className="size-4 text-quaternary" />
        </span>
      </Tooltip>
    </Text>
  );
};

export const TraceUsageBasedPlanFeatureTextWithTooltip = ({
  planType,
  showUsageCounter = false,
}: {
  planType: PaymentPlanCardTiers;
  showUsageCounter?: boolean;
}) => {
  const ttlDurations = useInstanceFlagOrDefault(
    InstanceFlags.trace_tier_duration_days
  ) as Record<string, number>;

  const { total: traceCount, isLoading } = useTraceCount();

  const { isCurrentPlan } = useShowPlanStatusBadge({ cardPlanType: planType });

  const mainText = (
    <Text as="span">
      Up to {formatter.format(PAYMENT_PLAN_PRICING_MAP[planType].numFreeTraces)}
      {''} traces/month, then{' '}
      <Text as="span" weight="medium">
        usage-based
      </Text>
    </Text>
  );

  const tooltipItems = [
    `$${
      PAYMENT_PLAN_PRICING_MAP[planType].shortLivedPricePerTrace * 1000
    } per 1K base retention traces. (${xCount(
      'day',
      ttlDurations[TraceTTLTier.shortlived]
    )})`,
    `Additional $${
      PAYMENT_PLAN_PRICING_MAP[planType].longLivedPricePerTrace * 1000
    } per 1K trace retention upgrades. (${xCount(
      'day',
      ttlDurations[TraceTTLTier.longlived]
    )})`,
  ];

  return (
    <div className="flex flex-col gap-2">
      <PlanFeatureTextWithTooltip text={mainText} tooltipItems={tooltipItems} />
      {showUsageCounter &&
        isCurrentPlan &&
        (isLoading ? (
          <Skeleton className="my-0 h-4 w-full" />
        ) : (
          <div className="flex w-full items-center gap-1">
            <div className="flex h-3 w-full items-center overflow-hidden rounded-full bg-tertiary">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${
                    (traceCount /
                      PAYMENT_PLAN_PRICING_MAP[planType].numFreeTraces) *
                    100
                  }%`,
                  backgroundColor: 'var(--purple-500)',
                }}
              />
            </div>
            <Text
              variant="md"
              weight="normal"
              color="primary"
              className="whitespace-nowrap"
            >
              {traceCount} /{' '}
              {PAYMENT_PLAN_PRICING_MAP['developer'].numFreeTraces}
            </Text>
          </div>
        ))}
    </div>
  );
};

export const DeploymentUsageBasedPlanFeatureTextWithTooltip = ({
  planType,
}: {
  planType: PaymentPlanCardTiers;
}) => {
  const mainText = (
    <Text as="span">
      Access to Deployment with{' '}
      <Text as="span" weight="medium">
        usage-based rates
      </Text>
    </Text>
  );

  const tooltipItems = [
    `$${PAYMENT_PLAN_PRICING_MAP[planType].pricePerNodeExecution} / node execution`,
    `$${PAYMENT_PLAN_PRICING_MAP[planType].pricePerUptimeMinuteDev} / min per Development deployment`,
    `$${PAYMENT_PLAN_PRICING_MAP[planType].pricePerUptimeMinuteProd} / min per Production deployment`,
  ];

  return (
    <PlanFeatureTextWithTooltip text={mainText} tooltipItems={tooltipItems} />
  );
};
